import { useEffect, useState } from 'react';

import { convert12HoursTo24Hours, convert24HoursTo12Hours } from 'core/datetime';

import Dialog from './Dialog';
import { Button } from './UtilElements';

const DOWN_ARROW_KEYCODE = 40;
const UP_ARROW_KEYCODE = 38;

interface TimePickerDialogProps {
  isOpen: boolean;
  value: TimeIn24Hours;
  onClose(value: TimeIn24Hours): void;
}

type TimePickerDialogState = TimeIn12Hours;

const initialState: TimePickerDialogState = {
  hours: 7,
  minutes: 0,
  meridiem: 'AM',
};

const TimePickerDialog = ({ isOpen, onClose, value }: TimePickerDialogProps) => {
  const [time, setTime] = useState<TimePickerDialogState>(initialState);

  useEffect(
    function initPage() {
      setTime(convert24HoursTo12Hours(value));
    },
    [value]
  );

  const goUp = (key: 'hours' | 'minutes') => {
    if (key === 'hours') {
      let h = time.hours;
      if (h !== 12) {
        h++;
      } else {
        h = 1;
      }
      setTime((prev) => ({ ...prev, hours: h }));
    } else {
      let m = time.minutes;
      if (m < 59) {
        m++;
      } else {
        m = 0;
      }
      setTime((prev) => ({ ...prev, minutes: m }));
    }
  };

  const goDown = (key: 'hours' | 'minutes') => {
    if (key === 'hours') {
      let h = time.hours;
      if (h > 1) {
        h--;
      } else {
        h = 12;
      }
      setTime((prev) => ({ ...prev, hours: h }));
    } else {
      let m = time.minutes;
      if (m > 0) {
        m--;
      } else {
        m = 59;
      }
      setTime((prev) => ({ ...prev, minutes: m }));
    }
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>, key: 'hours' | 'minutes') => {
    if (e.keyCode === UP_ARROW_KEYCODE) {
      goUp(key);
      e.preventDefault();
    } else if (e.keyCode === DOWN_ARROW_KEYCODE) {
      goDown(key);
      e.preventDefault();
    }
  };

  const setHours = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = +e.target.value;
    if (value >= 0 && value <= 12) {
      setTime((prev) => ({ ...prev, hours: value }));
    } else {
      setTime((prev) => ({ ...prev, hours: 0 }));
    }
  };

  const setMinutes = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = +e.target.value;
    if (value >= 0 && value <= 59) {
      setTime((prev) => ({ ...prev, minutes: value }));
    } else {
      setTime((prev) => ({ ...prev, minutes: 0 }));
    }
  };

  const changeTimeFormat = () => {
    setTime((prev) => ({ ...prev, meridiem: prev.meridiem === 'AM' ? 'PM' : 'AM' }));
  };

  return (
    <>
      <Dialog
        variant='auto-width'
        isOpen={isOpen}
        classes={{ body: 'timepicker__body' }}
        footer={
          <>
            <Button
              label='CANCEL'
              type='secondary'
              onRipple={() => {
                onClose(value);
                setTime(convert24HoursTo12Hours(value));
              }}
            />
            <Button label='SET' type='success' onRipple={() => onClose(convert12HoursTo24Hours(time))} />
          </>
        }
      >
        <div className='timepicker__col'>
          <span className='timepicker__control up' onClick={() => goUp('hours')} />
          <input
            type='number'
            className='timepicker__input'
            onKeyDown={(e) => onKeyDown(e, 'hours')}
            value={time.hours}
            onChange={setHours}
          />
          <span className='timepicker__control down' onClick={() => goDown('hours')} />
        </div>
        <div className='timepicker__col--seperator'>:</div>
        <div className='timepicker__col'>
          <span className='timepicker__control up' onClick={() => goUp('minutes')} />
          <input
            type='number'
            className='timepicker__input'
            onKeyDown={(e) => onKeyDown(e, 'minutes')}
            value={time.minutes}
            onChange={setMinutes}
          />
          <span className='timepicker__control down' onClick={() => goDown('minutes')} />
        </div>
        <div className='timepicker__col'>
          <span className='timepicker__control up' onClick={changeTimeFormat} />
          <input readOnly className='timepicker__input' value={time.meridiem} />
          <span className='timepicker__control down' onClick={changeTimeFormat} />
        </div>
      </Dialog>
    </>
  );
};
export default TimePickerDialog;
